import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export type RubricLeniency = "strict" | "balanced" | "lenient";

export interface RubricLevel {
  level: string;
  score: number;
  description: string;
  example?: string;
}

export interface RubricCriterion {
  leniency?: RubricLeniency;
  levels: RubricLevel[];
}

export interface Rubric {
  id: number;
  name: string;
  code: string;
  maxScore: number;
  criterion: RubricCriterion;
  activeFlag: boolean;
  createdDate: string;
  lastModified?: string;
}

export interface CreateRubricRequest {
  name: string;
  code: string;
  maxScore?: number;
  criterion: RubricCriterion;
}

export interface UpdateRubricRequest {
  name?: string;
  code?: string;
  maxScore?: number;
  criterion?: RubricCriterion;
  activeFlag?: boolean;
}

export interface RubricListResponse {
  success: boolean;
  data: {
    rubrics: Rubric[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface RubricResponse {
  success: boolean;
  data?: Rubric;
  message?: string;
  error?: string;
}

/**
 * Service for managing rubrics (CRUD operations)
 */
class RubricService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/rubrics`;
  }

  /**
   * Get all rubrics with optional pagination and search
   */
  async getAllRubrics(params?: {
    page?: number;
    limit?: number;
    search?: string;
    includeInactive?: boolean;
  }): Promise<RubricListResponse> {
    try {
      const response = await myAxios.get(this.baseUrl, { params });
      return response.data;
    } catch (error: any) {
      console.error("[RubricService] Error fetching rubrics:", error);
      return {
        success: false,
        data: {
          rubrics: [],
          pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
        },
      };
    }
  }

  /**
   * Get a single rubric by ID
   */
  async getRubricById(id: number): Promise<RubricResponse> {
    try {
      const url = `${this.baseUrl}/${id}`;
      const response = await myAxios.get(url);
      return response.data;
    } catch (error: any) {
      console.error("[RubricService] Error fetching rubric:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch rubric",
        error: error.message,
      };
    }
  }

  /**
   * Create a new rubric
   */
  async createRubric(rubric: CreateRubricRequest): Promise<RubricResponse> {
    try {
      const response = await myAxios.post(this.baseUrl, rubric);
      return response.data as unknown as RubricResponse;
    } catch (error: any) {
      console.error("[RubricService] Error creating rubric:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to create rubric",
        error: error.message,
      };
    }
  }

  /**
   * Update an existing rubric
   */
  async updateRubric(
    id: number,
    rubric: UpdateRubricRequest
  ): Promise<RubricResponse> {
    try {
      const url = `${this.baseUrl}/${id}`;
      const response = await myAxios.put(url, rubric);
      return response.data as unknown as RubricResponse;
    } catch (error: any) {
      console.error("[RubricService] Error updating rubric:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to update rubric",
        error: error.message,
      };
    }
  }

  /**
   * Delete a rubric (soft delete)
   */
  async deleteRubric(id: number): Promise<RubricResponse> {
    try {
      const url = `${this.baseUrl}/${id}`;
      const response = await myAxios.delete(url);
      return response.data;
    } catch (error: any) {
      console.error("[RubricService] Error deleting rubric:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete rubric",
        error: error.message,
      };
    }
  }

  /**
   * Generate a code from rubric name
   */
  generateCode(name: string): string {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .substring(0, 50);
  }

  /**
   * Validate rubric data
   */
  validateRubric(rubric: CreateRubricRequest | UpdateRubricRequest): string | null {
    if ("name" in rubric && rubric.name !== undefined) {
      if (!rubric.name || rubric.name.trim() === "") {
        return "Rubric name is required";
      }
    }

    if ("code" in rubric && rubric.code !== undefined) {
      if (!rubric.code || rubric.code.trim() === "") {
        return "Rubric code is required";
      }
      if (!/^[A-Z0-9_]+$/i.test(rubric.code)) {
        return "Code can only contain letters, numbers, and underscores";
      }
    }

    if ("criterion" in rubric && rubric.criterion !== undefined) {
      if (!rubric.criterion.levels || !Array.isArray(rubric.criterion.levels)) {
        return "Rubric must have at least one level";
      }
      if (rubric.criterion.levels.length === 0) {
        return "Rubric must have at least one level";
      }

      for (const level of rubric.criterion.levels) {
        if (!level.level || level.level.trim() === "") {
          return "Each level must have a name";
        }
        if (typeof level.score !== "number" || level.score < 0) {
          return "Each level must have a valid score (0 or higher)";
        }
        if (!level.description || level.description.trim() === "") {
          return "Each level must have a description";
        }
      }
    }

    if ("maxScore" in rubric && rubric.maxScore !== undefined) {
      if (typeof rubric.maxScore !== "number" || rubric.maxScore <= 0) {
        return "Max score must be a positive number";
      }
    }

    return null;
  }

  /**
   * Get default rubric template
   */
  getDefaultTemplate(): CreateRubricRequest {
    return {
      name: "",
      code: "",
      maxScore: 100,
      criterion: {
        leniency: "balanced",
        levels: [
          {
            level: "Excellent",
            score: 100,
            description: "Demonstrates exceptional understanding and mastery",
          },
          {
            level: "Good",
            score: 75,
            description: "Shows solid understanding with minor gaps",
          },
          {
            level: "Satisfactory",
            score: 50,
            description: "Meets basic requirements with some areas for improvement",
          },
          {
            level: "Needs Improvement",
            score: 25,
            description: "Shows limited understanding, requires additional work",
          },
          {
            level: "Unsatisfactory",
            score: 0,
            description: "Does not meet minimum requirements",
          },
        ],
      },
    };
  }
}

export default new RubricService();
